import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { AppDispatch, RootState } from '../../../store'

import { GroupType, createDefaults, formatData } from './helpers'
import {
  getCustomFieldGroups,
  postCustomFieldGroup,
  putCustomFieldGroup,
  deleteCustomFieldGroup as deleteCustomFieldGroupAPI
} from '../../../lib/api'
import { AddErrorAlert, AddSuccessAlert } from '../../alert'

type CustomFieldGroupState = {
  groups: Record<GroupType, APICustomFieldGroup[]>
  loaded: Record<GroupType, boolean>
  loading: Record<GroupType, boolean>
}

const initialState: CustomFieldGroupState = {
  groups: createDefaults([]),
  loaded: createDefaults(false),
  loading: createDefaults(false)
}

const customFieldGroupsSlice = createSlice({
  name: 'customFieldGroups',
  initialState,
  reducers: {
    startLoad: (
      state: CustomFieldGroupState,
      action: PayloadAction<GroupType>
    ) => {
      state.loading[action.payload] = true
    },

    finishLoad: (
      state: CustomFieldGroupState,
      action: PayloadAction<{ type: GroupType; data: APICustomFieldGroup[] }>
    ) => {
      const { type, data } = action.payload
      state.groups[type] = data.map(formatData)
      state.loaded[type] = true
      state.loading[type] = false
    },

    updatePartial: (
      state: CustomFieldGroupState,
      action: PayloadAction<APICustomFieldGroup[]>
    ) => {
      action.payload.forEach(item => {
        const group = formatData(item)
        const type = group.object_type

        // Add or replace based on payload ids
        const idx = state.groups[type].findIndex(other => other.id === group.id)
        if (idx >= 0) {
          state.groups[type][idx] = group
        } else {
          state.groups[type].push(group)
        }

        state.loaded[type] = true
        state.loading[type] = false
      })
    },

    deleteGroup: (
      state: CustomFieldGroupState,
      action: PayloadAction<string>
    ) => {
      Object.entries(state.groups).forEach(([type, groups]) => {
        state.groups[type] = groups.filter(group => group.id !== action.payload)
      })
    }
  }
})

const { startLoad, finishLoad, updatePartial, deleteGroup } =
  customFieldGroupsSlice.actions

export const fetchCustomFieldGroup = (type: GroupType) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(startLoad(type))
      const groups = await getCustomFieldGroups(type)
      dispatch(finishLoad({ type, data: groups }))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const createCustomFieldGroup = (item: CustomFieldGroupPOSTUpdate) => {
  return async (dispatch: AppDispatch) => {
    try {
      const group = await postCustomFieldGroup(item)
      if (group) {
        dispatch(updatePartial([group]))
      }
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const batchUpdateCustomFieldGroups = (
  items: CustomFieldGroupPUTUpdate[]
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const results = await Promise.all(
        items.map(async item => {
          const { id, ...rest } = item
          const group = await putCustomFieldGroup(id, rest)
          return group
        })
      )
      const filtered = results.filter(Boolean)
      if (filtered.length > 0) {
        dispatch(updatePartial(filtered))
        dispatch(AddSuccessAlert('Changes saved', 500))
      }
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const updateCustomFieldGroup = (item: CustomFieldGroupPUTUpdate) => {
  return async (dispatch: AppDispatch) => {
    dispatch(batchUpdateCustomFieldGroups([item]))
  }
}

export const deleteCustomFieldGroup = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await deleteCustomFieldGroupAPI(id)
      dispatch(deleteGroup(id))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectCustomFieldGroups = (state: RootState) =>
  state.customFieldGroups

export default customFieldGroupsSlice.reducer
